import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { getSEOData } from '../config/seo';
import { trackEvent, trackButtonClick } from '../components/GoogleAnalytics';
import { 
  Target, 
  Eye, 
  Users, 
  Globe, 
  Shield, 
  Zap, 
  Award, 
  TrendingUp,
  Play,
  ArrowRight,
  Briefcase,
  Clock,
  HeadphonesIcon,
  Database,
  Cloud,
  Lock,
  Building2
} from 'lucide-react';
import { YOUTUBE_CHANNEL, SUCCESS_STORY_VIDEOS, createYouTubeEmbedUrl, handleMissingVideo } from '../config/videos';

const About = () => {
  const seoData = getSEOData('/about');

  // Track page view
  useEffect(() => {
    trackEvent('page_view', 'engagement', 'about_page');
  }, []);
  const coreValues = [
    {
      icon: Zap,
      title: 'Innovation Excellence',
      description: 'Leading the industry with cutting-edge AI and automation technologies that transform business operations.',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-grade security and compliance standards protecting your most sensitive business data.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Users,
      title: 'Customer Success',
      description: 'Dedicated to ensuring every client achieves measurable ROI and operational excellence.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: TrendingUp,
      title: 'Continuous Growth',
      description: 'Constantly evolving our platform to meet the changing needs of modern enterprises.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Serving enterprises across 50+ countries with localized support and compliance.',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      icon: Award,
      title: 'Industry Leadership',
      description: 'Recognized by leading analysts as a visionary in business automation and digital transformation.',
      color: 'from-red-500 to-pink-500'
    }
  ];

  const enterpriseStats = [
    {
      number: 'Global',
      label: 'Enterprise Clients',
      description: 'Fortune 500 companies trust our platform'
    },
    {
      number: '1000+',
      label: 'Automation Projects',
      description: 'Successfully delivered and deployed'
    },
    {
      number: '99.9%',
      label: 'Platform Uptime',
      description: 'Enterprise-grade reliability guarantee'
    },
    {
      number: '24/7',
      label: 'Expert Support',
      description: 'Global support across all time zones'
    }
  ];

  const leadershipTeam = [
    // {
    //   name: 'Rajesh Patel',
    //   role: 'Chief Executive Officer',
    //   background: 'Former VP of Digital Transformation at Microsoft',
    //   image: '👨‍💼'
    // },
    // {
    //   name: 'Sarah Chen',
    //   role: 'Chief Technology Officer',
    //   background: 'Ex-Principal Engineer at Salesforce',
    //   image: '👩‍💻'
    // },
    // {
    //   name: 'Michael Rodriguez',
    //   role: 'Chief Revenue Officer',
    //   background: 'Former Enterprise Sales Director at Oracle',
    //   image: '👨‍💼'
    // },
    // {
    //   name: 'Dr. Priya Sharma',
    //   role: 'Chief Innovation Officer',
    //   background: 'PhD in AI, Former Research Lead at IBM',
    //   image: '👩‍🔬'
    // }
  ];

  const certifications = [
    { name: 'Enterprise Security', icon: Shield },
    { name: 'Advanced Encryption', icon: Lock },
    { name: 'GDPR Compliant', icon: Database },
    { name: 'Cloud Security Alliance', icon: Cloud }
  ];

  return (
    <div className="min-h-screen">
      <SEO {...seoData} />
      {/* Hero Section - Enterprise Focus */}
      <section className="relative bg-gradient-to-br from-primary/10 via-white to-secondary/5 py-20 lg:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-40"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              {/* Industry Recognition Badge */}
              <div className="inline-flex items-center bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full px-6 py-2 mb-6">
                <Award className="h-4 w-4 text-primary mr-2" />
                <span className="text-sm font-semibold text-gray-700">Industry Leader in Business Automation</span>
              </div>

              <h1 className="text-4xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight mb-6">
                Your Composable Enterprise
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">
                  Architecture Partner
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-600 mb-8 leading-relaxed">
                We enable composable business architecture through modular, API-first platforms. Build with packaged 
                business capabilities that adapt to change, scale independently, and accelerate innovation.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a
                  href="/about#success-stories"
                  onClick={() => trackButtonClick('success_stories', 'about_hero')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
                >
                  Our Success Stories
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </motion.a>
                
                <motion.a
                  href={YOUTUBE_CHANNEL.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick('company_overview_video', 'about_hero')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-primary hover:text-primary transition-colors duration-300 flex items-center justify-center group"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Company Overview
                </motion.a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Company Visual */}
              <img
                src="/images/Modern_conference_room_with_diverse_professional_t_1750596126.png"
                alt="Professional Team Leadership"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enterprise Stats */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {enterpriseStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl lg:text-5xl font-bold text-primary mb-2">{stat.number}</div>
                <div className="text-lg font-semibold text-gray-900 mb-1">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision - Enterprise Focus */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 p-8 rounded-2xl">
                <div className="flex items-center mb-6">
                  <div className="bg-gradient-to-r from-primary-500 to-secondary-500 w-12 h-12 rounded-lg flex items-center justify-center mr-4">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed">
                  To democratize enterprise automation by providing AI-powered, no-code solutions that enable 
                  organizations of all sizes to transform their operations, accelerate growth, and achieve 
                  unprecedented levels of efficiency and innovation.
                </p>
              </div>

              <div className="bg-gradient-to-br from-secondary/5 to-accent/5 p-8 rounded-2xl">
                <div className="flex items-center mb-6">
                  <div className="bg-gradient-to-r from-secondary to-accent w-12 h-12 rounded-lg flex items-center justify-center mr-4">
                    <Eye className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed">
                  To be the global leader in intelligent business automation, creating a world where every 
                  organization can harness the power of AI and automation to unlock their full potential 
                  and drive meaningful impact in their industries.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Enterprises Choose Aquargin</h3>
                <div className="space-y-4">
                  {[
                    { icon: Briefcase, text: "Proven track record with Fortune 500 companies" },
                    { icon: Clock, text: "Average 60% reduction in operational costs" },
                    { icon: TrendingUp, text: "300% average ROI within first year" },
                    { icon: HeadphonesIcon, text: "24/7 dedicated enterprise support" },
                    { icon: Shield, text: "Bank-grade security and compliance" },
                    { icon: Globe, text: "Global deployment and localization" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-gray-700">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values - Enterprise Focus */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Enterprise Values That Drive Us
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our core values shape every decision we make and every solution we build for our enterprise clients.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className={`w-16 h-16 rounded-lg bg-gradient-to-r ${value.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <value.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section id="leadership" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              World-Class Leadership Team
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our leadership team brings decades of experience from leading technology companies and enterprise software organizations.
            </p>
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            {leadershipTeam.map((leader, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
              >
                <div className="text-6xl mb-4">{leader.image}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{leader.name}</h3>
                <p className="text-primary font-semibold mb-3">{leader.role}</p>
                <p className="text-sm text-gray-600">{leader.background}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section id="success-stories" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Client Success Stories
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear directly from our enterprise clients about their transformation journey with Aquargin.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {Object.values(SUCCESS_STORY_VIDEOS).map((video, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group cursor-pointer"
                onClick={() => window.open(YOUTUBE_CHANNEL.url, '_blank')}
              >
                <div className="relative bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={createYouTubeEmbedUrl(video.id)}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute top-0 left-0 w-full h-full rounded-xl"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-sm font-medium text-gray-800">{video.company}</span>
                  </div>
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full">
                    <span className="text-sm font-bold">{video.metric}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{video.title}</h3>
                  <p className="text-gray-600">{video.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Join the Leaders in Business Automation
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Ready to transform your enterprise operations? Connect with our team to discover how 
              Aquargin can accelerate your digital transformation journey.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-primary px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Schedule Executive Briefing
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="/pricing"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary transition-colors duration-300 flex items-center justify-center group"
              >
                <Building2 className="mr-2 h-5 w-5" />
                Enterprise Demo
              </motion.a>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ Dedicated account manager  ✓ Custom implementation plan  ✓ Enterprise SLA guarantee</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About; 